import type { NativeEvent, WebEvent } from "~/types/events";

// Infer payload type for a given WebEvent type
type PayloadOfWebEvent<TType extends WebEvent["type"]> = Extract<
  WebEvent,
  { type: TType }
> extends { payload: infer P }
  ? P
  : undefined;

// Infer payload type for a given NativeEvent type
type PayloadOfNativeEvent<TType extends NativeEvent["type"]> = Extract<
  NativeEvent,
  { type: TType }
> extends { payload: infer P }
  ? P
  : undefined;

export type AwaitEventOptions<
  S extends NativeEvent["type"],
  E extends NativeEvent["type"],
  W extends WebEvent["type"]
> = {
  timeoutMs: number;
  successEvent: S;
  errorEvent: E;
  sendEvent: W;
  payload?: PayloadOfWebEvent<W>;
};

function sendWebEvent(event: WebEvent) {
  const postMessage = JSON.stringify(event);
  window.ReactNativeWebView?.postMessage(postMessage);
}

function isSuccessEvent<S extends NativeEvent["type"]>(
  event: NativeEvent,
  successEvent: S
): event is Extract<NativeEvent, { type: S }> {
  return event.type === successEvent;
}

export function awaitNativeEventOnce<
  S extends NativeEvent["type"],
  E extends NativeEvent["type"],
  W extends WebEvent["type"]
>(options: AwaitEventOptions<S, E, W>): Promise<PayloadOfNativeEvent<S>> {
  return new Promise<PayloadOfNativeEvent<S>>((resolve, reject) => {
    let settled = false;
    const { timeoutMs, successEvent, errorEvent, sendEvent, payload } = options;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      document.removeEventListener("message", onMessage as EventListener);
      window.clearTimeout(timeoutId);
    };

    const onMessage = (event: MessageEvent) => {
      if (settled) return;
      if (typeof event.data !== "string") return;
      try {
        const nativeEvent = JSON.parse(event.data) as NativeEvent;

        if (isSuccessEvent(nativeEvent, successEvent)) {
          settled = true;
          cleanup();
          const successPayload = (
            nativeEvent as unknown as { payload?: unknown }
          ).payload as PayloadOfNativeEvent<S>;
          resolve(successPayload);
        }

        if (nativeEvent.type === errorEvent) {
          settled = true;
          cleanup();
          const errPayload = (nativeEvent as unknown as { payload?: unknown })
            .payload;
          let message = "Unknown error";
          if (typeof errPayload === "string") {
            message = errPayload;
          } else if (
            errPayload &&
            typeof errPayload === "object" &&
            "error" in (errPayload as Record<string, unknown>)
          ) {
            const maybeErr = (errPayload as { error?: unknown }).error;
            if (typeof maybeErr === "string") message = maybeErr;
          }
          reject(new Error(message));
        }
      } catch {
        // ignore non-native events
      }
    };

    window.addEventListener("message", onMessage);
    document.addEventListener("message", onMessage as EventListener);

    const eventToSend = (
      payload === undefined ? { type: sendEvent } : { type: sendEvent, payload }
    ) as Extract<WebEvent, { type: W }>;
    sendWebEvent(eventToSend);

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error("Operation timed out"));
    }, timeoutMs) as unknown as number;
  });
}
